import type { CellViewWire } from '@badger-sentinel/protocol';
import { useMemo } from 'react';

import { DetailBlock } from './DetailBlock.js';
import { DetectionLadder } from './DetectionLadder.js';
import { EnvironmentBlock, bandOf } from './EnvironmentBlock.js';
import { PinTray } from './PinTray.js';
import { PublishControl } from './PublishControl.js';
import { RailFrame } from './RailFrame.js';
import { RangeList } from './RangeList.js';
import { ConfirmInline } from '../chrome/ConfirmInline.js';
import { MilSymbol } from '../chrome/MilSymbol.js';
import { TrackActions, TrackPanel } from '../mw/TrackPanel.js';
import { OrderBuilder } from '../orders/OrderBuilder.js';
import { orderableAssets } from '../model/builder.js';
import { affiliationOf } from '../model/sidc.js';
import {
  assetRows,
  bandDetailRows,
  cyberContactRows,
  networkDetailRows,
  contactRows,
  ordersFor,
  rangeOriginOf,
  rangesFrom,
  regionRows,
  resolveSelection,
  type ActiveOrder,
  type ResolvedSelection,
  type Selection,
} from '../model/selection.js';
import type { QueuedOrder } from '../model/queue.js';
import { useConsoleStore } from '../state/store.js';

/**
 * The right rail — selection, environment, ladder, active orders, CTA, tray
 * (design 1152–1605).
 *
 * Four subjects: asset · contact · region · **launch event**. Everything below
 * the header is driven by `resolveSelection`, which resolves a `{kind, id}` from
 * the store against whatever snapshot is current — so the selection survives a
 * resolution (M5-D9) and a subject that has left the view says so instead of
 * vanishing. That last property is why Phase 4's track detail lives here rather
 * than in the MW pane: a snapshot lands on **every stage arrival**, and a panel
 * that rebuilt itself from the view would lose the operator's place four times a
 * window.
 *
 * Presentational (M5-D2). The map callbacks Phase 5 will wire — focus, and
 * click-to-fill — are **declared and left unwired** rather than stubbed: a stub
 * that silently does nothing is a control that teaches a player the console is
 * unresponsive, and one that does something plausible is a thing Phase 5 has to
 * unpick.
 */

export interface SelectionRailProps {
  readonly view: CellViewWire | null;
  readonly serverNowMs: number;
  readonly onMwAction: (
    eventId: string,
    action: 'IDENTIFY' | 'RELEASE' | 'DISMISS',
    identity?: string,
  ) => void;
  /** Both from `railLayout`, computed once by the shell against the live viewport. */
  readonly width: number;
  readonly maxWidth: number;
  readonly onContextMenu: (selection: Selection, x: number, y: number) => void;
  /** The builder is withdrawn with the commit control, for the same reason (Q20). */
  readonly canOrder: boolean;
  /**
   * Read-only **by rule** (RB §5.1), not merely "cannot order right now".
   *
   * `canOrder` is false for a closed window too, and the two need different
   * words: one is a wait, the other is never.
   */
  readonly readOnly: boolean;
  /** `publish.contact` — an SDA/ISR seat handing a contact to the rest of its team. */
  readonly onPublish: (contact: string) => void;
  /**
   * The MW board is drawing the track's reading itself, so this rail should not.
   *
   * The shell decides, because only the shell knows both which tab is up and
   * whether that tab's map is mounted. See `TrackPanel`'s `readingInPane`.
   */
  readonly mwReadingInPane: boolean;
}

export function SelectionRail(props: SelectionRailProps): JSX.Element {
  const open = useConsoleStore((state) => state.railRightOpen);
  const toggleRail = useConsoleStore((state) => state.toggleRail);
  const setRailWidth = useConsoleStore((state) => state.setRailWidth);
  const selection = useConsoleStore((state) => state.selection);
  const select = useConsoleStore((state) => state.select);
  const pins = useConsoleStore((state) => state.pins);
  const togglePin = useConsoleStore((state) => state.togglePin);
  const unpin = useConsoleStore((state) => state.unpin);
  const compareOpen = useConsoleStore((state) => state.compareOpen);
  const setCompareOpen = useConsoleStore((state) => state.setCompareOpen);
  const batch = useConsoleStore((state) => state.batch);
  const toggleBatch = useConsoleStore((state) => state.toggleBatch);
  const clearBatch = useConsoleStore((state) => state.clearBatch);
  const queue = useConsoleStore((state) => state.queue);
  const builder = useConsoleStore((state) => state.builder);
  const editOrder = useConsoleStore((state) => state.editOrder);
  const cancelOrder = useConsoleStore((state) => state.cancelOrder);
  const cancelOrdersFor = useConsoleStore((state) => state.cancelOrdersFor);
  const applyBatch = useConsoleStore((state) => state.applyBatch);
  const confirm = useConsoleStore((state) => state.confirm);
  const requestConfirm = useConsoleStore((state) => state.requestConfirm);
  const clearConfirm = useConsoleStore((state) => state.clearConfirm);
  const showToast = useConsoleStore((state) => state.showToast);
  const setMwRefOpen = useConsoleStore((state) => state.setMwRefOpen);

  const resolved = useMemo(() => resolveSelection(props.view, selection), [props.view, selection]);
  const origin = rangeOriginOf(resolved);
  const pinned = selection !== null && pins.some((pin) => pin.id === selection.id);

  // The batch holds whatever was ⌘-clicked — contacts and regions included — so
  // APPLY works on the intersection with what this seat may task, and is absent
  // when that intersection is empty.
  const view = props.view;
  const eligible = useMemo(
    () =>
      view === null
        ? []
        : orderableAssets(view)
            .map((asset) => asset.id)
            .filter((id) => batch.includes(id)),
    [view, batch],
  );

  return (
    <RailFrame
      side="right"
      label="SELECTION"
      open={open}
      width={props.width}
      maxWidth={props.maxWidth}
      onToggle={() => toggleRail('right')}
      onResize={(next) => setRailWidth('right', next)}
    >
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}
      >
        <Subject
          view={props.view}
          resolved={resolved}
          queue={queue}
          origin={origin}
          serverNowMs={props.serverNowMs}
          onMwAction={props.onMwAction}
          onOpenTable={() => setMwRefOpen(true)}
          mwReadingInPane={props.mwReadingInPane}
          onSelectRegion={(region) => select({ kind: 'REGION', id: region })}
          onEditOrder={editOrder}
          onCancelOrder={cancelOrder}
          confirmKey={confirm?.kind === 'cancel-order' ? confirm.key : null}
          confirmSubject={confirm?.kind === 'cancel-orders' ? confirm.subject : null}
          onCancelAll={cancelOrdersFor}
          onRequestCancel={(key) => requestConfirm({ kind: 'cancel-order', key })}
          onKeep={clearConfirm}
          onPublish={props.onPublish}
        />
      </div>

      {/* Pinned under the scroll region, so IDENTIFY, the two dispositions and
          the confirm are on screen whatever the operator has scrolled to. Under a
          five-minute clock a control that has to be found is a control that is
          not there. */}
      {resolved.kind === 'MW_TRACK' && props.view !== null && (
        <TrackActions
          track={resolved.track}
          catalogue={props.view.mwCatalogue}
          serverNowMs={props.serverNowMs}
          onAction={props.onMwAction}
          onOpenTable={() => setMwRefOpen(true)}
        />
      )}

      {/* PIN, BATCH and the subject menu are about *taskable* subjects: a launch
          event cannot be compared in the tray or given a mission, so it is not
          offered either. */}
      {selection !== null && resolved.kind !== 'NONE' && resolved.kind !== 'MW_TRACK' && (
        <div
          data-testid="selection-actions"
          style={{
            flex: 'none',
            display: 'flex',
            alignItems: 'stretch',
            gap: 6,
            height: 34,
            padding: '4px 12px',
            background: 'var(--surface-2)',
            borderTop: '1px solid var(--border-strong)',
          }}
        >
          <button
            type="button"
            data-testid="selection-pin"
            onClick={() => togglePin(selection)}
            style={ACTION}
          >
            {pinned ? 'UNPIN' : 'PIN'}
          </button>
          <button
            type="button"
            data-testid="selection-batch"
            onClick={() => toggleBatch(selection.id)}
            style={ACTION}
          >
            {batch.includes(selection.id) ? 'UNBATCH' : 'BATCH'}
          </button>
          <button
            type="button"
            data-testid="selection-actions-menu"
            aria-label="ALL ACTIONS"
            onClick={(event) => props.onContextMenu(selection, event.clientX, event.clientY)}
            style={{ ...ACTION, flex: 'none', width: 30 }}
          >
            ⋯
          </button>
        </div>
      )}

      <PinTray
        view={props.view}
        pins={pins}
        compareOpen={compareOpen}
        batch={batch}
        onSelect={select}
        onUnpin={unpin}
        onToggleCompare={() => setCompareOpen(!compareOpen)}
        onClearBatch={clearBatch}
        // APPLY needs a mission to apply and something to apply it to. A button
        // that can only fail is a button that teaches the console is broken.
        canApply={props.canOrder && builder.mission !== null && eligible.length > 0}
        applyCount={eligible.length}
        confirming={confirm?.kind === 'batch-apply'}
        onRequestApply={() => requestConfirm({ kind: 'batch-apply' })}
        onApply={() => {
          const made = applyBatch(eligible);
          if (made > 0) showToast(String(made) + ' ORDERS QUEUED');
        }}
        onKeepBatch={clearConfirm}
      />

      <OrderBuilder view={props.view} enabled={props.canOrder} readOnly={props.readOnly} />
    </RailFrame>
  );
}

function Subject(props: {
  readonly view: CellViewWire | null;
  readonly resolved: ResolvedSelection;
  readonly queue: readonly QueuedOrder[];
  readonly origin: string | null;
  readonly serverNowMs: number;
  readonly onMwAction: (
    eventId: string,
    action: 'IDENTIFY' | 'RELEASE' | 'DISMISS',
    identity?: string,
  ) => void;
  readonly onOpenTable: () => void;
  readonly mwReadingInPane: boolean;
  readonly onSelectRegion: (region: string) => void;
  readonly onEditOrder: (key: string) => void;
  readonly onCancelOrder: (key: string) => void;
  readonly confirmKey: string | null;
  /** The `unit` menu's "CANCEL ITS ORDERS" — several at once, one question. */
  readonly confirmSubject: string | null;
  readonly onCancelAll: (subjectId: string) => void;
  readonly onRequestCancel: (key: string) => void;
  readonly onKeep: () => void;
  readonly onPublish: (contact: string) => void;
}): JSX.Element {
  const { view, resolved } = props;

  if (resolved.kind === 'NONE') return <Empty text="NOTHING SELECTED" />;
  if (resolved.kind === 'MISSING') {
    // A real state, and it says which subject went: a panel that emptied itself
    // on a turn advance reads as a console that lost its place.
    return <Empty text={resolved.ref.id + ' NOT IN THIS PICTURE'} />;
  }
  if (view === null) return <Empty text="NO PICTURE" />;

  const ranges = props.origin === null ? [] : rangesFrom(view, props.origin);

  if (resolved.kind === 'MW_TRACK') {
    return (
      <TrackPanel
        track={resolved.track}
        catalogue={view.mwCatalogue}
        serverNowMs={props.serverNowMs}
        onAction={props.onMwAction}
        onOpenTable={props.onOpenTable}
        readingInPane={props.mwReadingInPane}
      />
    );
  }

  if (resolved.kind === 'ASSET') {
    const asset = resolved.asset;
    return (
      <>
        <DetailBlock
          id={asset.id}
          idColour="var(--blue)"
          chip={{ text: asset.status, colour: statusColour(asset.status) }}
          name={asset.typeName}
          kindLabel={asset.orderable ? 'FRIENDLY ASSET · ORDERABLE' : 'FRIENDLY ASSET'}
          symbol={
            <MilSymbol
              typeId={asset.typeId}
              segment={asset.segment}
              affiliation={affiliationOf(view.viewer.team, view.viewer.team)}
              colour="var(--blue)"
            />
          }
          rows={assetRows(asset)}
        />
        <EnvironmentBlock
          view={view}
          region={asset.location.kind === 'GROUND' ? asset.location.region : null}
          band={bandOf(asset.location.kind === 'ORBIT' ? asset.location.band : null)}
        />
        <ActiveOrders
          orders={ordersFor(props.queue, asset.id)}
          subjectId={asset.id}
          onEdit={props.onEditOrder}
          onCancel={props.onCancelOrder}
          confirmKey={props.confirmKey}
          confirmSubject={props.confirmSubject}
          onCancelAll={props.onCancelAll}
          onRequestCancel={props.onRequestCancel}
          onKeep={props.onKeep}
        />
        {props.origin !== null && (
          <RangeList origin={props.origin} entries={ranges} onSelect={props.onSelectRegion} />
        )}
      </>
    );
  }

  if (resolved.kind === 'CONTACT') {
    const contact = resolved.contact;
    return (
      <>
        <DetailBlock
          id={contact.id}
          idColour="var(--red)"
          name={contact.presentedTypeName}
          kindLabel="CONTACT"
          symbol={
            <MilSymbol
              typeId={contact.presentedType}
              segment={contact.location.kind === 'REGION' ? 'GROUND' : 'SPACE'}
              affiliation="HOSTILE"
              rung={contact.rung}
              colour="var(--red)"
            />
          }
          rows={contactRows(contact)}
        >
          <DetectionLadder rung={contact.rung} />
        </DetailBlock>
        <PublishControl
          contact={contact}
          canPublish={view.capabilities.canPublish}
          onPublish={props.onPublish}
        />
        <EnvironmentBlock
          view={view}
          region={contact.location.kind === 'REGION' ? contact.location.region : null}
          band={bandOf(
            contact.location.kind === 'BAND' || contact.location.kind === 'SLOT'
              ? contact.location.band
              : null,
          )}
        />
        <ActiveOrders
          orders={ordersFor(props.queue, contact.id)}
          subjectId={contact.id}
          onEdit={props.onEditOrder}
          onCancel={props.onCancelOrder}
          confirmKey={props.confirmKey}
          confirmSubject={props.confirmSubject}
          onCancelAll={props.onCancelAll}
          onRequestCancel={props.onRequestCancel}
          onKeep={props.onKeep}
        />
        {props.origin !== null && (
          <RangeList origin={props.origin} entries={ranges} onSelect={props.onSelectRegion} />
        )}
      </>
    );
  }

  if (resolved.kind === 'BAND') {
    // A band is a zone, and the rail asks a zone the same questions. `RangeList`
    // is absent on purpose: `map.ranges` is a table of *ground* pairs, and band
    // separation is a rule (`bandSeparation`) rather than a distance — putting a
    // range list under a ring would be the one place this console could be read
    // as measuring one (must-not #6).
    return (
      <>
        <DetailBlock
          id={resolved.band}
          // A band's id is its name. `LEO` expanded to `LOW EARTH ORBIT` would be
          // a word the wire does not carry and an operator does not need.
          name=""
          kindLabel="ORBITAL BAND"
          rows={bandDetailRows(view, resolved.band, resolved.slots)}
        />
        <EnvironmentBlock view={view} region={null} band={resolved.band} />
        <ActiveOrders
          orders={ordersFor(props.queue, resolved.band)}
          subjectId={resolved.band}
          onEdit={props.onEditOrder}
          onCancel={props.onCancelOrder}
          confirmKey={props.confirmKey}
          confirmSubject={props.confirmSubject}
          onCancelAll={props.onCancelAll}
          onRequestCancel={props.onRequestCancel}
          onKeep={props.onKeep}
        />
      </>
    );
  }

  if (resolved.kind === 'CYBER_CONTACT') {
    // A machine a foothold revealed and nothing has looked at. Five rows and no
    // detection ladder: RB §5.6.2 keeps it out of `contacts[]` precisely because
    // it has not been found, and a ladder drawn at its floor would say FOUND.
    return (
      <>
        <DetailBlock
          id={resolved.contact.alias}
          name=""
          kindLabel="CYBER CONTACT"
          rows={cyberContactRows(resolved.contact)}
        />
        <ActiveOrders
          orders={ordersFor(props.queue, resolved.contact.alias)}
          subjectId={resolved.contact.alias}
          onEdit={props.onEditOrder}
          onCancel={props.onCancelOrder}
          confirmKey={props.confirmKey}
          confirmSubject={props.confirmSubject}
          onCancelAll={props.onCancelAll}
          onRequestCancel={props.onRequestCancel}
          onKeep={props.onKeep}
        />
      </>
    );
  }

  if (resolved.kind === 'NETWORK') {
    // An enclave is a zone, and the rail asks a zone the same questions. Two
    // things are deliberately absent: `RangeList`, because `map.ranges` is a
    // table of ground pairs and nothing on this wire measures a hop; and
    // `EnvironmentBlock`, because weather and debris are conditions of places
    // and an enclave is not one.
    return (
      <>
        <DetailBlock
          id={resolved.network.id}
          name={resolved.network.name}
          kindLabel="CYBER ENCLAVE"
          rows={networkDetailRows(view, resolved.network)}
        />
        <ActiveOrders
          orders={ordersFor(props.queue, resolved.network.id)}
          subjectId={resolved.network.id}
          onEdit={props.onEditOrder}
          onCancel={props.onCancelOrder}
          confirmKey={props.confirmKey}
          confirmSubject={props.confirmSubject}
          onCancelAll={props.onCancelAll}
          onRequestCancel={props.onRequestCancel}
          onKeep={props.onKeep}
        />
      </>
    );
  }

  const region = resolved.region;
  return (
    <>
      <DetailBlock
        id={region.id}
        name={region.name}
        kindLabel="REGION"
        rows={regionRows(view, region)}
      />
      <EnvironmentBlock view={view} region={region.id} band={null} />
      <ActiveOrders
        orders={ordersFor(props.queue, region.id)}
        subjectId={region.id}
        onEdit={props.onEditOrder}
        onCancel={props.onCancelOrder}
        confirmKey={props.confirmKey}
        confirmSubject={props.confirmSubject}
        onCancelAll={props.onCancelAll}
        onRequestCancel={props.onRequestCancel}
        onKeep={props.onKeep}
      />
      <RangeList origin={region.id} entries={ranges} onSelect={props.onSelectRegion} />
    </>
  );
}

/**
 * Orders standing against this subject this turn (design 1357–1382).
 *
 * Phase 2 rendered the mission and its counterpart and nothing else. The row now
 * carries the design's `EDIT` and its cancel — and the cancel goes through the
 * confirm, because an order removed under a five-minute clock is not coming back
 * unless the operator remembers what it was.
 */
function ActiveOrders(props: {
  readonly orders: readonly ActiveOrder[];
  readonly subjectId: string;
  readonly onEdit: (key: string) => void;
  readonly onCancel: (key: string) => void;
  readonly confirmKey: string | null;
  readonly confirmSubject: string | null;
  readonly onCancelAll: (subjectId: string) => void;
  readonly onRequestCancel: (key: string) => void;
  readonly onKeep: () => void;
}): JSX.Element | null {
  if (props.orders.length === 0) return null;
  return (
    <div
      data-testid="active-orders"
      style={{
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <span
        style={{
          padding: '10px 14px 5px',
          fontFamily: 'var(--font-data)',
          fontSize: 11,
          letterSpacing: '.14em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
        }}
      >
        TASKED THIS TURN
      </span>
      {props.orders.map((order) => (
        <div
          key={order.key}
          data-testid="active-order"
          data-order-key={order.key}
          style={{
            display: 'flex',
            flexDirection: 'column',
            borderTop: '1px solid var(--rule-faint)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              minHeight: 32,
              padding: '4px 8px 4px 14px',
            }}
          >
            <span
              style={{
                flex: 'none',
                fontFamily: 'var(--font-ui)',
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: '.1em',
                textTransform: 'uppercase',
                color: 'var(--primary)',
              }}
            >
              {order.mission}
            </span>
            <span
              style={{
                flex: 1,
                minWidth: 0,
                textAlign: 'right',
                fontFamily: 'var(--font-data)',
                fontSize: 11,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: 'var(--text-3)',
              }}
            >
              {order.counterpart}
            </span>
            <button
              type="button"
              data-testid="active-order-edit"
              title="REOPEN IN BUILDER"
              onClick={() => props.onEdit(order.key)}
              style={SMALL}
            >
              EDIT
            </button>
            <button
              type="button"
              data-testid="active-order-cancel"
              aria-label={'CANCEL ORDER ' + order.mission}
              onClick={() => props.onRequestCancel(order.key)}
              style={{ ...SMALL, width: 21, padding: 0 }}
            >
              ×
            </button>
          </div>
          {props.confirmKey === order.key && (
            <ConfirmInline
              testId="active-order-confirm"
              prompt="Cancel this order?"
              confirmLabel="CANCEL"
              cancelLabel="KEEP"
              onConfirm={() => props.onCancel(order.key)}
              onCancel={props.onKeep}
            />
          )}
        </div>
      ))}
      {props.confirmSubject === props.subjectId && (
        <ConfirmInline
          testId="active-orders-confirm"
          prompt="Cancel every order on this unit?"
          detail={String(props.orders.length) + ' ORDERS'}
          confirmLabel="CANCEL ALL"
          cancelLabel="KEEP"
          onConfirm={() => props.onCancelAll(props.subjectId)}
          onCancel={props.onKeep}
        />
      )}
    </div>
  );
}

const SMALL = {
  flex: 'none',
  height: 21,
  padding: '0 7px',
  background: 'transparent',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  color: 'var(--text-3)',
  fontFamily: 'var(--font-data)',
  fontSize: 10,
  letterSpacing: '.1em',
  lineHeight: 1,
  cursor: 'pointer',
} as const;

function Empty(props: { readonly text: string }): JSX.Element {
  return (
    <div
      data-testid="selection-empty"
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        textAlign: 'center',
        fontFamily: 'var(--font-data)',
        fontSize: 11,
        letterSpacing: '.14em',
        color: 'var(--text-muted)',
      }}
    >
      {props.text}
    </div>
  );
}

function statusColour(status: string): string {
  switch (status) {
    case 'NOMINAL':
      return 'var(--ok)';
    case 'DEGRADED':
      return 'var(--warn)';
    default:
      return 'var(--red-text)';
  }
}

const ACTION = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  color: 'var(--text-3)',
  fontFamily: 'var(--font-data)',
  fontSize: 10.5,
  letterSpacing: '.12em',
  cursor: 'pointer',
} as const;
